import { useRef, useState } from 'react';
import type { AppDownloadBannerProps } from '../components/AppDownloadBanner';
import { analyticsClient, trackEvent } from '../utils/analytics';
import { devicePlatform, isInNativeApp } from '../utils/deviceUtils';
import { STORE_LINKS } from '../constants/storeLinks';

const HIDDEN_UNTIL_KEY = 'app-banner-hidden-until';
const DAY_MS = 24 * 60 * 60 * 1000;
// Short after a store tap: he may not have installed. A gps-app connection hides it for good.
const HIDE_AFTER_OPEN_MS = 2 * DAY_MS;
const HIDE_AFTER_DISMISS_MS = 7 * DAY_MS;
// L'Oréal staff have their own L'Oréal Luxe / DPGP apps: gps-app is not for them — except
// the Flagship desk, which uses no dedicated app.
const LOREAL_EMAIL = /@loreal\.com$/i;
const FLAGSHIP_SERVICE_DESK_ID = 403;

interface AppDownloadBannerInput {
  pathname: string;
  email: string | null;
  serviceDeskId: number | null;
  hasMobileApp: boolean;
  isImpersonating: boolean;
  role: string;
  serviceDeskName: string;
}

const isHidden = (): boolean => {
  try {
    return Date.now() < Number(localStorage.getItem(HIDDEN_UNTIL_KEY));
  } catch {
    return false;
  }
};

const persistHiddenFor = (durationMs: number): void => {
  try {
    localStorage.setItem(HIDDEN_UNTIL_KEY, String(Date.now() + durationMs));
  } catch {
    // ponytail: blocked storage, the banner stays hidden for this session only
  }
};

const track = (eventName: string, props: Record<string, string>): void => {
  try {
    trackEvent(eventName, { props });
  } catch {
    // An adblocker must never break the link
  }
};

export const useAppDownloadBanner = ({
  pathname,
  email,
  serviceDeskId,
  hasMobileApp,
  isImpersonating,
  role,
  serviceDeskName,
}: AppDownloadBannerInput): AppDownloadBannerProps | null => {
  const [dismissed, setDismissed] = useState(isHidden);
  const openedRef = useRef(false);
  const platform = devicePlatform();
  const isTaskRoute = pathname.startsWith('/request') || pathname.includes('/forms/');
  const usesLorealApp = LOREAL_EMAIL.test((email ?? '').trim()) && serviceDeskId !== FLAGSHIP_SERVICE_DESK_ID;

  if (platform === 'desktop' || dismissed || hasMobileApp || isImpersonating || isTaskRoute || usesLorealApp || isInNativeApp()) return null;

  const props = { platform, role, client: analyticsClient(serviceDeskName) };

  const hide = (eventName: string, durationMs: number): void => {
    track(eventName, props);
    persistHiddenFor(durationMs);
  };

  return {
    url: STORE_LINKS[platform],
    onOpen: (event) => {
      if (openedRef.current) {
        event.preventDefault();
        return;
      }
      openedRef.current = true;
      hide('App Download Click', HIDE_AFTER_OPEN_MS);
      // Deferred so the anchor is still mounted when the browser follows it
      setTimeout(() => setDismissed(true), 0);
    },
    onDismiss: () => {
      // Already counted and persisted (2 days) by the open, whose deferred hide has not run yet
      if (!openedRef.current) hide('App Download Dismiss', HIDE_AFTER_DISMISS_MS);
      setDismissed(true);
    },
  };
};
